import { Button, Modal, TextArea, Typography, Checkbox } from '@scale/scaleui';
import { SquadMemberRole, SquadType } from '@scale/shared/types/expertManagement';
import { useCallback, useMemo, useState } from 'react';
import { CurrentWorkerAssignmentTable } from 'react-scale/components/ExpertManagement/ExpertManagementPage/CommunityManagement/CommunityManagementTab';
import { useAddWorkersToSquad } from 'react-scale/components/ExpertManagement/hooks/mutations/useAddWorkersToSquad';
import { useFetchWorkersManagers } from 'react-scale/components/ExpertManagement/hooks/queries/useFetchWorkersManagers';
import { defaultApiFetchErrorHandler } from 'react-scale/lib/api-fetch';
import { useToggle } from 'react-use-v13';

interface AddWorkersToSquadModalProps {
  onClose: () => void;
  open: boolean;
  refetch: () => Promise<void>;
  squadId: string;
  role: SquadMemberRole.Contributor | SquadMemberRole.TeamLead;
  squadType: SquadType | undefined;
}

export function AddWorkersToSquadModal({
  onClose,
  open,
  refetch,
  squadId,
  role,
  squadType,
}: AddWorkersToSquadModalProps) {
  const [workerIds, setWorkerIds] = useState<string>('');
  const [openMoveToNewManagerModal, setOpenMoveToNewManagerModal] = useState<boolean>(false);
  const [createDiscourseCommunityChannel, toggleCreateDiscourseCommunityChannel] = useToggle(true);

  const [isUploading, setIsUploading] = useState<boolean>(false);

  const canUpload = useMemo(() => !isUploading && workerIds.length > 0, [workerIds, isUploading]);

  const { mutateAsync: addWorkersToSquad, isLoading: addWorkersLoading } = useAddWorkersToSquad();

  const workerIdsArray = useMemo(
    () => workerIds.split(/[^a-zA-Z0-9]+/).filter(id => id.length > 0),
    [workerIds],
  );

  const { loading, workerManagers } = useFetchWorkersManagers(
    openMoveToNewManagerModal,
    workerIdsArray,
  );

  const closeModal = useCallback(() => {
    setWorkerIds('');
    setOpenMoveToNewManagerModal(false);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    setIsUploading(true);
    try {
      const result = await addWorkersToSquad({
        squadId,
        workerIds: workerIdsArray,
        role,
        createDiscourseCommunityChannel,
      });
      if (result.success) {
        await refetch();
        closeModal();
      } else {
        defaultApiFetchErrorHandler({ name: 'error', message: result.error, logError: () => null });
      }
    } catch (e) {
      defaultApiFetchErrorHandler(e, { handleHttp: true });
    } finally {
      setIsUploading(false);
    }
  }, [
    addWorkersToSquad,
    squadId,
    workerIdsArray,
    role,
    createDiscourseCommunityChannel,
    refetch,
    closeModal,
  ]);

  return (
    <>
      <Modal
        title={`Add ${
          role === SquadMemberRole.Contributor ? 'Contributors' : 'Team Leads'
        } To Squad`}
        open={open}
        onClose={closeModal}
      >
        <div className="flex flex-col gap-2">
          <Typography.Paragraph2>
            Enter a list of {role === SquadMemberRole.Contributor ? 'contributor' : 'team lead'} IDs
            to add to this squad.
          </Typography.Paragraph2>
          <TextArea
            value={workerIds}
            onChange={e => setWorkerIds(e.target.value)}
            style={{ height: 200 }}
          />
          {role === SquadMemberRole.TeamLead && squadType === SquadType.Community && (
            <Typography.Paragraph3>
              WARNING: If this team lead is in charge of any squads, <br /> all of their CBs will be
              moved into this Community squad. <br />
              <span className="font-bold">This action cannot be undone.</span>
            </Typography.Paragraph3>
          )}
          {role === SquadMemberRole.TeamLead && (
            <Checkbox
              className="modal-child"
              label="Create a Discourse community channel for each team lead's squad"
              checked={createDiscourseCommunityChannel}
              onChange={toggleCreateDiscourseCommunityChannel}
            />
          )}
          <div>
            <Button
              disabled={!canUpload || addWorkersLoading}
              onClick={() => setOpenMoveToNewManagerModal(true)}
            >
              Add {role === SquadMemberRole.Contributor ? 'Contributors' : 'Team Leads'}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        title="Confirm Worker Assignment"
        open={openMoveToNewManagerModal}
        onClose={() => setOpenMoveToNewManagerModal(false)}
        actions={
          <div className="flex items-center gap-2 justify-end">
            <Button
              disabled={isUploading}
              onClick={() => setOpenMoveToNewManagerModal(false)}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button disabled={isUploading} onClick={handleSubmit}>
              Assign Workers ({workerIdsArray.length})
            </Button>
          </div>
        }
        theme="scale-theme"
      >
        <div className="flex flex-col gap-2 min-h-[240px]">
          <Typography.Subtitle3 className="mb-2">
            Are you sure you want to assign the following workers?
          </Typography.Subtitle3>
          <CurrentWorkerAssignmentTable
            workerIds={workerIdsArray}
            workerManagers={workerManagers}
            loading={loading}
          />
        </div>
      </Modal>
    </>
  );
}
